import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { PieChart } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { InfoTooltip } from './info-tooltip'
import { CATEGORY_META, CATEGORY_ORDER } from './content-categories'

type CategoryRow = RouterOutput['analytics']['contentMix']['byCategory'][number]
type AggregatedRow = Omit<CategoryRow, 'platform'>

export function ContentMixCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading, error } = trpc.analytics.contentMix.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  const grouped = data?.byCategory ? groupByPlatform(data.byCategory) : new Map()
  // Content categories are platform-agnostic concepts ("personal story"
  // exists everywhere) — when scope is multi-platform, aggregate the rows
  // across platforms instead of stacking sub-sections per platform.
  const isMultiPlatform = grouped.size > 1
  const aggregated = isMultiPlatform && data?.byCategory ? aggregateMix(data.byCategory) : null

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <PieChart size={14} className="text-muted-foreground" />
          Content Mix
          <InfoTooltip>
            Distribution of content types you actually post. Pair this with Content Type Impact to
            spot mix vs. performance gaps — categories you under-post but that perform well are the
            biggest opportunities.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRow />}

        {error && (
          <p className="text-muted-foreground py-4 text-xs">
            Couldn't load this breakdown. Please refresh.
          </p>
        )}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            No classified posts yet — content categories are filling in as new posts arrive.
          </p>
        )}

        {data && data.hasEnoughData && aggregated && (
          <MixRow rows={orderByCategoryTaxonomy(aggregated)} />
        )}

        {data && data.hasEnoughData && !aggregated && (
          <div className="space-y-3">
            {[...grouped.entries()].map(([platform, rows]) => (
              <div key={platform}>
                <MixRow rows={orderByCategoryTaxonomy(rows)} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function MixRow({ rows }: { rows: { category: string; postCount: number; share: number }[] }) {
  const total = rows.reduce((s, r) => s + r.postCount, 0)
  return (
    <div>
      {/* Stacked bar: each segment proportional to category share. Single
          row so even a 4-category brand reads at a glance. */}
      <div className="bg-muted mb-3 flex h-2 overflow-hidden rounded-full">
        {rows.map((r) => {
          const meta = CATEGORY_META[r.category]
          return (
            <div
              key={r.category}
              className="h-full transition-all"
              style={{
                width: `${r.share * 100}%`,
                backgroundColor: meta?.color ?? 'hsl(var(--primary))',
              }}
              title={`${meta?.label ?? r.category} — ${(r.share * 100).toFixed(0)}%`}
            />
          )
        })}
      </div>
      <ul className="space-y-1.5">
        {rows.map((r) => {
          const meta = CATEGORY_META[r.category]
          return (
            <li key={r.category} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: meta?.color ?? 'hsl(var(--primary))' }}
                />
                <span className="text-foreground">{meta?.label ?? r.category}</span>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-muted-foreground/70 text-[10px]">{r.postCount}</span>
                <span className="text-foreground font-medium tabular-nums">
                  {(r.share * 100).toFixed(0)}%
                </span>
              </div>
            </li>
          )
        })}
      </ul>
      <p className="text-muted-foreground/70 mt-2 text-[10px]">{total} classified posts</p>
    </div>
  )
}

function SkeletonRow() {
  return (
    <div>
      <div className="bg-muted mb-3 h-2 animate-pulse rounded-full" />
      <div className="space-y-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="bg-muted h-3 w-24 animate-pulse rounded" />
            <div className="bg-muted h-3 w-12 animate-pulse rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}

/** Sum post counts per category across platforms; recompute share against
 *  the cross-platform total. Categories that don't appear in any platform's
 *  rows are absent from the result. */
function aggregateMix(rows: CategoryRow[]): AggregatedRow[] {
  const totals = new Map<string, number>()
  for (const r of rows) totals.set(r.category, (totals.get(r.category) ?? 0) + r.postCount)
  const total = [...totals.values()].reduce((s, n) => s + n, 0)
  return [...totals.entries()].map(([category, postCount]) => ({
    category,
    postCount,
    share: total > 0 ? postCount / total : 0,
  }))
}

function orderByCategoryTaxonomy<T extends { category: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ai = (CATEGORY_ORDER as readonly string[]).indexOf(a.category)
    const bi = (CATEGORY_ORDER as readonly string[]).indexOf(b.category)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
}
